import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import RichContent from "@/components/RichContent";
import { Button } from "@/components/ui/button";
import { isSafeUrl, type PageBlock } from "@/lib/pages/schema";
import { useLanguage } from "@/i18n/LanguageContext";
import { renderNative } from "./nativeRegistry";

const BG: Record<string, string> = { default: "", muted: "bg-muted/40", dark: "bg-foreground text-background", brand: "bg-primary text-primary-foreground" };
const PAD: Record<string, string> = { none: "py-0", sm: "py-6 md:py-8", md: "py-12 md:py-16", lg: "py-16 md:py-24" };
const WIDTH: Record<string, string> = { narrow: "max-w-3xl", normal: "max-w-5xl", wide: "max-w-7xl" };
const HIDE: Record<string, string> = { none: "", mobile: "hidden md:block", desktop: "md:hidden" };

export function styleClasses(style: PageBlock["style"] = {}) {
  return [BG[style.background ?? "default"], PAD[style.padding ?? "md"], style.align === "center" ? "text-center" : "", HIDE[style.hideOn ?? "none"]].filter(Boolean).join(" ");
}
/** Wrapper classes for built-in sections (native blocks and the locked page body): no default padding, width applied. */
export function nativeStyleClasses(style: PageBlock["style"] = {}) {
  return [styleClasses({ padding: "none", ...style }), style.width ? `mx-auto w-full ${WIDTH[style.width]}` : ""].filter(Boolean).join(" ");
}
export const hasCustomStyle = (style: PageBlock["style"] = {}) => Object.values(style).some((v) => v && v !== "default" && v !== "none");

function SmartLink({ href, children, variant = "default" }: { href?: string; children: ReactNode; variant?: "default" | "outline" }) {
  const { localizedPath } = useLanguage();
  if (!href || !isSafeUrl(href)) return null;
  if (href.startsWith("/")) return <Button asChild variant={variant}><Link to={localizedPath(href)}>{children}</Link></Button>;
  const external = /^https?:/.test(href);
  return <Button asChild variant={variant}><a href={href} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{children}</a></Button>;
}
const img = (src?: string) => (src && isSafeUrl(src) ? src : undefined);

function videoEmbed(url: string): string | null {
  const yt = url.match(/^https:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,20})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  const vm = url.match(/^https:\/\/(?:www\.)?vimeo\.com\/(\d{4,12})/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

/** Renders one typed block. Native blocks delegate to built-in components that keep their own business logic. */
export function BlockView({ block, headingLevel = 2 }: { block: PageBlock; headingLevel?: 1 | 2 }) {
  const H = (headingLevel === 1 ? "h1" : "h2") as "h1" | "h2";
  const width = WIDTH[block.style?.width ?? "normal"];
  const wrap = (inner: ReactNode) => (
    <section id={block.id} data-block-type={block.type} className={styleClasses(block.style)}>
      <div className={`container mx-auto px-4 ${width}`}>{inner}</div>
    </section>
  );
  switch (block.type) {
    case "hero": {
      const p = block.props;
      return wrap(
        <div className={`grid gap-8 ${img(p.image_url) ? "md:grid-cols-2 items-center" : ""}`}>
          <div>
            {p.eyebrow && <p className="eyebrow mb-3">{p.eyebrow}</p>}
            <H className="text-4xl md:text-5xl font-extrabold leading-tight mb-4">{p.heading}</H>
            {p.subheading && <p className="text-lg opacity-80 mb-6">{p.subheading}</p>}
            <div className={`flex flex-wrap gap-3 ${block.style?.align === "center" ? "justify-center" : ""}`}>
              {p.cta_label && <SmartLink href={p.cta_href}>{p.cta_label}</SmartLink>}
              {p.secondary_label && <SmartLink href={p.secondary_href} variant="outline">{p.secondary_label}</SmartLink>}
            </div>
          </div>
          {img(p.image_url) && <img src={img(p.image_url)} alt={p.image_alt ?? ""} className="w-full rounded-lg border border-border" loading="lazy" />}
        </div>,
      );
    }
    case "rich_text":
      return wrap(<>{block.props.heading && <H className="text-3xl font-bold mb-6">{block.props.heading}</H>}<RichContent content={block.props.markdown} format="markdown" className="prose prose-neutral dark:prose-invert max-w-none" /></>);
    case "image":
      return wrap(<figure>{img(block.props.image_url) && <img src={img(block.props.image_url)} alt={block.props.image_alt} className="w-full rounded-lg" loading="lazy" />}{block.props.caption && <figcaption className="mt-2 text-sm opacity-70">{block.props.caption}</figcaption>}</figure>);
    case "cards": {
      const cols = { 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-2 lg:grid-cols-4" }[block.props.columns ?? 3];
      return wrap(
        <>
          {block.props.heading && <H className="text-3xl font-bold mb-3">{block.props.heading}</H>}
          {block.props.intro && <p className="opacity-80 mb-8 max-w-3xl">{block.props.intro}</p>}
          <div className={`grid gap-6 ${cols}`}>
            {block.props.items.map((it, i) => (
              <div key={i} className="rounded-lg border border-border bg-card text-card-foreground p-6 text-left">
                {img(it.image_url) && <img src={img(it.image_url)} alt={it.image_alt ?? ""} className="mb-4 w-full rounded" loading="lazy" />}
                <h3 className="font-semibold text-lg mb-2">{it.title}</h3>
                {it.body && <p className="text-sm text-muted-foreground">{it.body}</p>}
                {it.href && <div className="mt-4"><SmartLink href={it.href} variant="outline">→</SmartLink></div>}
              </div>
            ))}
          </div>
        </>,
      );
    }
    case "cta":
      return wrap(<div className="rounded-xl border border-border p-8 md:p-12"><H className="text-3xl font-bold mb-3">{block.props.heading}</H>{block.props.body && <p className="opacity-80 mb-6">{block.props.body}</p>}<SmartLink href={block.props.href}>{block.props.label}</SmartLink></div>);
    case "faq":
      return wrap(<>{block.props.heading && <H className="text-3xl font-bold mb-6">{block.props.heading}</H>}<div className="divide-y divide-border">{block.props.items.map((f, i) => <details key={i} className="py-4"><summary className="cursor-pointer font-medium">{f.q}</summary><p className="mt-2 text-muted-foreground whitespace-pre-line">{f.a}</p></details>)}</div></>);
    case "media": {
      const embed = videoEmbed(block.props.video_url);
      return wrap(
        <figure>
          {embed ? <div className="aspect-video"><iframe src={embed} title={block.props.caption || "Video"} className="h-full w-full rounded-lg" allow="encrypted-media; picture-in-picture" allowFullScreen loading="lazy" /></div>
            : isSafeUrl(block.props.video_url) && /^https:/.test(block.props.video_url) ? <video src={block.props.video_url} poster={img(block.props.poster_url)} controls className="w-full rounded-lg" /> : null}
          {block.props.caption && <figcaption className="mt-2 text-sm opacity-70">{block.props.caption}</figcaption>}
        </figure>,
      );
    }
    case "native":
      return renderNative(block);
  }
}

export function BlockList({ blocks, firstHeadingLevel = 2 }: { blocks: PageBlock[]; firstHeadingLevel?: 1 | 2 }) {
  return <>{blocks.filter((b) => b.visible !== false).map((b, i) => <BlockView key={b.id} block={b} headingLevel={i === 0 ? firstHeadingLevel : 2} />)}</>;
}
